/**
 * Service API pour les System Alerts
 */

import type { Alert, PerformanceMetrics } from '../types/alerts.types';
import { fetchWithAuth, safeJsonParse, API_URL } from '@/utils/api';

const API_BASE = `${API_URL}/superadmin/monitoring`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await safeJsonParse(response).catch(() => ({ message: 'Request failed' }));
    throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || 'Request failed');
  }
  return safeJsonParse(response);
}

export const alertsAPI = {
  /**
   * Get performance metrics
   */
  async getPerformanceMetrics(): Promise<PerformanceMetrics> {
    const response = await fetchWithAuth(`${API_BASE}/performance`);
    return handleResponse<PerformanceMetrics>(response);
  },

  /**
   * Get active alerts (non-resolved)
   */
  async getActiveAlerts(): Promise<Alert[]> {
    const response = await fetchWithAuth(`${API_BASE}/alerts`);
    const data = await handleResponse<{ alerts: Alert[] }>(response);
    return data.alerts;
  },

  /**
   * Get all alerts (active and resolved)
   */
  async getAllAlerts(): Promise<Alert[]> {
    const response = await fetchWithAuth(`${API_BASE}/alerts/all`);
    const data = await handleResponse<{ alerts: Alert[] }>(response);
    return data.alerts;
  },

  /**
   * Resolve an alert
   */
  async resolveAlert(alertId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE}/alerts/${alertId}/resolve`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: 'Resolve failed' }));
      throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || 'Resolve failed');
    }
  },
};
